import ApiService, { RequestConfig } from 'services/server/ApiService';

const API_URL = 'https://financialmodelingprep.com/api/v3';
const API_URL_V4 = 'https://financialmodelingprep.com/api/v4';
const API_URL_STABLE = 'https://financialmodelingprep.com/stable';
const API_QUERY = `apikey=${process.env.STOCK_API_KEY}`;

// Transform key names to abbreviated format
const transformKey = (key: string): string => {
  // Handle price target keys
  if (key === 'symbol') return 't';
  if (key === 'lastMonthCount') return 'lmc';
  if (key === 'lastMonthAvgPriceTarget') return 'lmapt';
  if (key === 'lastQuarterCount') return 'lqc';
  if (key === 'lastQuarterAvgPriceTarget') return 'lqapt';
  if (key === 'lastYearCount') return 'lyc';
  if (key === 'lastYearAvgPriceTarget') return 'lyapt';
  if (key === 'allTimeCount') return 'atc';
  if (key === 'allTimeAvgPriceTarget') return 'atapt';
  if (key === 'publishers') return '__remove__';

  // Handle analyst rating keys
  if (key === 'strongBuy') return 'sb';
  if (key === 'buy') return 'b';
  if (key === 'hold') return 'h';
  if (key === 'sell') return 's';
  if (key === 'strongSell') return 'ss';
  if (key === 'consensus') return 'c';

  // Default behavior for any other keys using camelCase
  return key
    .replace(/([A-Z])/g, ' $1') // Add space before capital letters
    .split(' ') // Split by space (now each part is a word)
    .filter((part) => part) // Remove empty parts
    .map((part) => part.charAt(0).toLowerCase()) // Take first letter of each part
    .join(''); // Join them together
};

// Helper function to convert CSV to JSON
const csvToJson = (csv: string, transformKeys = false) => {
  const lines = csv.split('\n');
  const headers = lines[0].split(',').map((header) => header.replace(/^"/, '').replace(/"$/, ''));

  return lines
    .slice(1)
    .filter((line) => line.trim() !== '')
    .map((line) => {
      const values = line.match(/(".*?"|[^",\s]+)(?=\s*,|\s*$)/g) || [];
      const row: Record<string, any> = {};

      headers.forEach((header, i) => {
        // Apply key transformation if enabled
        const key = transformKeys ? transformKey(header) : header;

        // Skip keys marked for removal
        if (key === '__remove__') return;

        let value = values[i] || '';
        // Remove quotes
        value = value.replace(/^"/, '').replace(/"$/, '');

        // Try to parse JSON arrays/objects
        if (value.startsWith('[') || value.startsWith('{')) {
          try {
            value = JSON.parse(value);
          } catch (e) {
            // Keep as string if parsing fails
          }
        }

        // Try to convert to number if appropriate
        if (!isNaN(Number(value)) && value !== '') {
          row[key] = Number(value);
        } else {
          row[key] = value;
        }
      });

      return row;
    });
};

const api = new ApiService({
  baseUrl: API_URL,
});
const apiV4 = new ApiService({
  baseUrl: API_URL_V4,
});

const apiStable = new ApiService({
  baseUrl: API_URL_STABLE,
});

class Client {
  get(endpoint: string, config: RequestConfig = {}, queryStringArray: string[] = []) {
    const queryString = [...queryStringArray, API_QUERY].join('&');
    return api.get(`${endpoint}?${queryString}`, config);
  }

  async getCsv(
    endpoint: string,
    config: RequestConfig = {},
    queryStringArray: string[] = [],
    transformKeys = false,
  ) {
    const queryString = [...queryStringArray, API_QUERY].join('&');
    const url = `${API_URL}/${endpoint}?${queryString}`;

    const response = await fetch(url, {
      ...config,
      headers: {
        ...config.headers,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch CSV: ${response.statusText}`);
    }

    const csvText = await response.text();
    return csvToJson(csvText, transformKeys);
  }
}

const client = new Client();

class ClientV4 {
  get(endpoint: string, config: RequestConfig = {}, queryStringArray: string[] = []) {
    const queryString = [...queryStringArray, API_QUERY].join('&');
    return apiV4.get(`${endpoint}?${queryString}`, config);
  }

  async getCsv(
    endpoint: string,
    config: RequestConfig = {},
    queryStringArray: string[] = [],
    transformKeys = false,
  ) {
    const queryString = [...queryStringArray, API_QUERY].join('&');
    const url = `${API_URL_V4}/${endpoint}?${queryString}`;

    const response = await fetch(url, {
      ...config,
      headers: {
        ...config.headers,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch CSV: ${response.statusText}`);
    }

    const csvText = await response.text();
    return csvToJson(csvText, transformKeys);
  }
}

export const clientV4 = new ClientV4();

class ClientStable {
  get(endpoint: string, config: RequestConfig = {}, queryStringArray: string[] = []) {
    const queryString = [...queryStringArray, API_QUERY].join('&');
    return apiStable.get(`${endpoint}?${queryString}`, config);
  }

  async getCsv(
    endpoint: string,
    config: RequestConfig = {},
    queryStringArray: string[] = [],
    transformKeys = false,
  ) {
    const queryString = [...queryStringArray, API_QUERY].join('&');
    const url = `${API_URL_STABLE}/${endpoint}?${queryString}`;

    const response = await fetch(url, {
      ...config,
      headers: {
        ...config.headers,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch CSV: ${response.statusText}`);
    }

    const csvText = await response.text();
    return csvToJson(csvText, transformKeys);
  }
}

export const clientStable = new ClientStable();

export default client;
